import { useLocation, useNavigate } from 'react-router-dom'
import {
  useLazyGetUserInfoQuery,
  useLazyLoginQuery,
} from '../apis/auth/auth.api.ts'
import { useEffect } from 'react'
import {
  getLocalStorageItem,
  setLocalStorageItem,
} from '../utils/local-storage.ts'
import { useDispatch } from 'react-redux'
import {
  requestUserInfo,
  setAccessToken,
  setUserInfo,
} from '../features/user/user.slice.ts'
import { IdeyaSpinner } from '../common/components/ideya-spinner.tsx'
import { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import {
  NEW_RELIC,
  NEW_RELIC_AUTH_EVENT,
  newRelic,
} from '../utils/new-relic.ts'
import { getErrorDescription } from '../redux/utils.ts'

export const LoginPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [login, loginResult] = useLazyLoginQuery()
  const [getUserInfo, userInfoResult] = useLazyGetUserInfoQuery()
  //const isLoading = loginResult.isLoading || userInfoResult.isLoading
  const dispatch = useDispatch()

  useEffect(() => {
    login()
  }, [login])

  useEffect(() => {
    if (!loginResult.isFetching && loginResult.data) {
      if (
        loginResult &&
        loginResult.data.data &&
        loginResult.data.data.accessToken
      ) {
        newRelic.recordCustomEvent(
          NEW_RELIC_AUTH_EVENT.exchangeTokenSucceeded,
          { element: NEW_RELIC.AUTH.exchangeToken }
        )
        setLocalStorageItem('apiAccessToken', loginResult.data.data.accessToken)
        dispatch(setAccessToken(loginResult.data.data.accessToken))
        dispatch(requestUserInfo())
        getUserInfo()
      }
    }
  }, [dispatch, getUserInfo, loginResult])

  useEffect(() => {
    if (userInfoResult.isError) {
      newRelic.recordCustomEvent(NEW_RELIC_AUTH_EVENT.verifyTokenFailed, {
        element: NEW_RELIC.AUTH.verifyToken,
        event: getErrorDescription(userInfoResult.error),
      })
      newRelic.noticeError(
        new Error(getErrorDescription(userInfoResult.error)),
        { context: 'cloudflareVerifyToken' }
      )
    }
  }, [userInfoResult.isError, userInfoResult.error])

  useEffect(() => {
    if (loginResult.isSuccess && userInfoResult.isSuccess) {
      newRelic.recordCustomEvent(NEW_RELIC_AUTH_EVENT.verifyTokenSucceeded, {
        element: NEW_RELIC.AUTH.verifyToken,
      })
      dispatch(setUserInfo(userInfoResult.data.data))
      const disclaimerAccepted = getLocalStorageItem('disclaimerAccepted')
      // If role is null, then they are an internal user
      // Internal contributors do not see disclaimer page
      if (!disclaimerAccepted && userInfoResult.data.data.role === '') {
        if (userInfoResult.data.data.region === 'Global') {
          navigate('/login/global-disclaimer', {
            state: { from: location.pathname },
          })
        } else {
          navigate('/login/disclaimer', {
            state: { from: location.pathname },
          })
          return
        }
      } else {
        navigate(location.state?.from || '/idea')
      }
    } else if (loginResult.isError) {
      const error = loginResult.error as FetchBaseQueryError
      newRelic.recordCustomEvent(NEW_RELIC_AUTH_EVENT.exchangeTokenFailed, {
        element: NEW_RELIC.AUTH.exchangeToken,
        event: getErrorDescription(loginResult.error),
      })
      newRelic.noticeError(new Error(getErrorDescription(loginResult.error)), {
        context: 'cloudflareLogin',
      })
      if ('status' in error && error.status === 403) {
        navigate('/inactive')
      } else {
        navigate('/logout')
      }
    }
  }, [
    loginResult.isSuccess,
    userInfoResult.isSuccess,
    navigate,
    location.state?.from,
    dispatch,
    location.pathname,
    loginResult.isError,
    loginResult.error,
  ])

  return (
    <div
      style={{
        width: '100%',
        height: '50vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <IdeyaSpinner />
    </div>
  )
}
